// react
import { useCallback } from 'react';
// types
import { ReportFilterModelDto } from 'src/api';
import { IReportTemplateTableFilters } from 'src/types/report-template';
import { DateTime } from 'luxon';
// redux
import { useDispatch, useSelector } from 'src/redux/store';
import { setError } from 'src/redux/slices/error';
import { updateFilterModelItemValue, updateFilterModelItemTemplate } from 'src/redux/slices/report';
// parse
import { parseArrayToString, parseDateToString } from './string-parse';

export function VariableInjector(tableIndex: number) {
  const dispatch = useDispatch();

  const filterModel = useSelector(
    (stats) => stats.report.report.tables![tableIndex].filterModel
  ) as ReportFilterModelDto;
  const filter = useSelector((stats) => stats.report.filter) as IReportTemplateTableFilters;

  const handleFilterArrayInjection = useCallback(
    (variable: any[], itemIndex: number, template: string) => {
      try {
        const value = parseArrayToString(variable);
        dispatch(updateFilterModelItemValue({ tableIndex, itemIndex, value }));
        dispatch(updateFilterModelItemTemplate({ tableIndex, itemIndex, template }));
      } catch (e) {
        dispatch(setError(e));
      }
    },
    [dispatch, tableIndex]
  );

  const handleFilterDateInjection = useCallback(
    (variable: DateTime, itemIndex: number, template: string) => {
      try {
        const value = parseDateToString(variable);
        dispatch(updateFilterModelItemValue({ tableIndex, itemIndex, value }));
        dispatch(updateFilterModelItemTemplate({ tableIndex, itemIndex, template }));
      } catch (e) {
        dispatch(setError(e));
      }
    },
    [dispatch, tableIndex]
  );

  const handleFilterStringInjection = useCallback(
    (value: string, itemIndex: number, template: string) => {
      try {
        dispatch(updateFilterModelItemValue({ tableIndex, itemIndex, value }));
        dispatch(updateFilterModelItemTemplate({ tableIndex, itemIndex, template }));
      } catch (e) {
        dispatch(setError(e));
      }
    },
    [dispatch, tableIndex]
  );

  const handleFilterInjection = useCallback(
    (value: string, index: number) => {
      const variable = value.replace(/{%(.+)%}/gi, '$1').split('.');

      if (
        variable.length !== 2 ||
        filter[variable[1] as keyof IReportTemplateTableFilters] === null
      ) {
        return;
      }

      if (variable[0] === 'filter' && filter[variable[1] as keyof IReportTemplateTableFilters]) {
        if (Array.isArray(filter[variable[1] as keyof IReportTemplateTableFilters])) {
          handleFilterArrayInjection(
            filter[variable[1] as keyof IReportTemplateTableFilters] as any[],
            index,
            value
          );
        } else if (filter[variable[1] as keyof IReportTemplateTableFilters] instanceof DateTime) {
          handleFilterDateInjection(
            filter[variable[1] as keyof IReportTemplateTableFilters] as DateTime,
            index,
            value
          );
        } else if (filter[variable[1] as keyof IReportTemplateTableFilters] !== null) {
          handleFilterStringInjection(
            filter[variable[1] as keyof IReportTemplateTableFilters]!.toString(),
            index,
            value
          );
        } else {
          handleFilterStringInjection('', index, '');
        }
      }
    },
    [filter, handleFilterArrayInjection, handleFilterDateInjection, handleFilterStringInjection]
  );

  if (
    filter &&
    filterModel &&
    filterModel.items &&
    filterModel.items.length > 0 &&
    (filterModel.items.some((x) => typeof x.value === 'string' && x.value.match(/{%(.+)%}/gi)) ||
      filterModel.items.some(
        (x) => typeof x.template === 'string' && x.template.match(/{%(.+)%}/gi)
      ))
  ) {
    filterModel.items.forEach((x, i: number) => {
      if (typeof x.value === 'string' && x.value.match(/{%(.+)%}/gi)) {
        handleFilterInjection(x.value, i);
      } else if (typeof x.template === 'string' && x.template.match(/{%(.+)%}/gi)) {
        handleFilterInjection(x.template, i);
      }
      return true;
    });
  }
}
